import Control from '@/Controls/Control';
import ComponentManager from '@/Management/ComponentManager';
import PasswordBoxViewModelType from '@/ViewModels/Controls/PasswordBoxViewModel';
import LabelType from '@/Controls/Label';
import ImageViewModelBuilder from '@/Builder/ImageViewModelBuilder';
import Image from '@/Controls/Image';
import ImageViewModel from '@/ViewModels/Controls/ImageViewModel';

export default class PasswordBox extends Control {

    private Label = ComponentManager.getControl<typeof LabelType>('Label');
    private jQueryControl: JQuery<HTMLElement>;
    private revealPassword: JQuery<HTMLElement>;
    private $input: JQuery<HTMLInputElement>;

    private revealPasswordImage: ImageViewModel;

    public constructor(private viewModel: PasswordBoxViewModelType, private element?: JQuery) {
        super(viewModel, element);
        this.onWidgetCreated(this.onWidgetCreatedCallback.bind(this));
    }

    public getControl(): JQuery {
        if (this.jQueryControl != null) {
            return this.jQueryControl;
        }
        if (!this.element) {
            this.$input = $('<input type="password">') as JQuery<HTMLInputElement>;
            this.jQueryControl = $('<div>').append(this.$input);
        } else {
            this.jQueryControl = this.element;
            this.$input = this.jQueryControl.find('input') as JQuery<HTMLInputElement>;
        }

        if (this.viewModel.showPasswordIcon) {
            this.revealPassword = $('<div>');
            this.revealPassword.addClass('reveal-password-icon');

            this.revealPasswordImage = new ImageViewModelBuilder().withSource('eye.svg').build();
            const image = new Image(this.revealPasswordImage, this.revealPassword);
            image.getControl().insertAfter(this.$input);
        }

        if (this.viewModel.onSubmit != null) {
            this.addEventHandler('keypress', this.jQueryControl, (event) => {
                // 13 = Enter-Taste, 10 = LF (Line Feed) - etwas älter, aber kann noch relevant sein
                if (event.which === 13 || event.which === 10) {
                    this.viewModel.onSubmit(this.viewModel.value());
                }
            });
        }

        this.widget(null);

        return this.jQueryControl;
    }

    private onWidgetCreatedCallback(widget: null): void {
        this.applyBindings(this.jQueryControl, {
            controlName: 'passwordbox',

            loader: this.viewModel.isBusy,

            controlIdent: this.identifier,

            invalid: this.viewModel.invalidationText,

            visible: this.viewModel.isVisible,

            styles: this.definePureComputed({
                read: () => {
                    let styles = ['default'];

                    return styles;
                }
            }),

            states: this.definePureComputed({
                read: () => {
                    let states = [];

                    if (this.viewModel.isRequired() === true) {
                        states.push('required');
                    }

                    if (this.viewModel.isValid() === true) {
                        states.push('valid');
                    } else {
                        states.push('invalid');
                    }

                    if (this.viewModel.isEnabled() === true) {
                        states.push('enabled');
                    } else {
                        states.push('disabled');
                    }

                    return states;
                }
            })
        });

        if (this.viewModel.maxLength >= 0) {
            this.$input.attr('maxlength', this.viewModel.maxLength);
        }

        this.applyBindings(this.$input, {
            tooltip: this.viewModel.tooltip,

            focus: this.viewModel.isFocusable,

            classes: ['k-textbox'],

            attr: {
                name: this.viewModel.name,
                readonly: this.defineComputed(() => {
                    return !this.viewModel.isEnabled();
                }),
                placeholder: this.viewModel.placeholder
            },

            textInput: this.viewModel.value,

            locator: this.viewModel.locator
        });

        if (this.viewModel.onChange != null) {
            this.defineComputed(() => {
                this.viewModel.onChange(this.viewModel.value());
            });
        }

        if (this.viewModel.onKeyDown != null) {
            this.addEventHandler('keydown', this.jQueryControl, (event) => {
                this.viewModel.onKeyDown(event.keyCode);
            });
        }

        if (this.viewModel.onKeyUp != null) {
            this.addEventHandler('keyup', this.jQueryControl, (event) => {
                this.viewModel.onKeyUp(event.keyCode);
            });
        }

        if (!this.viewModel.autoFillEnabled) {
            this.applyBindings(this.$input,
                {
                    preventAutoFill: {
                        isEnabled: this.viewModel.isEnabled
                    }
                });
        }

        if (this.viewModel.showPasswordIcon) {
            this.addEventHandler('click', this.revealPassword, (event) => {
                const currentType = this.$input.get(0).type as string;
                if (currentType.toLowerCase() === 'password') {
                    this.revealPasswordImage?.source('eye_crossed_out.svg');
                    this.$input.prop('type', 'text');
                }
                else {
                    this.revealPasswordImage?.source('eye.svg');
                    this.$input.prop('type', 'password');
                }
            })
        }
    }

    public addLabelReference(label: JQuery): JQuery {
        let id = this.identifier + '-input';
        this.getControl().find('input').attr('id', id);
        label.attr('for', id);
        return label;
    }
}

ComponentManager.setControl('PasswordBox', PasswordBox, 'RADPresentation.HTMLClient.DefaultViewExtensions');